import { computed } from "vue";
import { useRoute } from "vue-router";

import { type Mode, groupForMode } from "@/composables/mode-navigation";

/**
 * Whether the current route is inside the **Design** group — Business,
 * Strategy, IT Landscape and the Overview. Build and Run (Live) are outside
 * it.
 *
 * Drafts stage changes to the model you design, so the whole Design group
 * shares them. An open draft stays open anywhere in the same stek, including
 * Build and Run. This flag only limits where editing starts a draft: Build
 * and Run are not editing surfaces, so starting an edit there does not
 * create one.
 *
 * Design routes declare no `meta.mode` of their own except IT Landscape's
 * "dev" (see NavSideBar's `?.meta.mode || "business"`), so the group — not the
 * mode — is the thing to ask about: `groupForMode` maps live → run, build →
 * build, and everything else → design.
 *
 * Shared because the drafts entry points each need the same gate, and a second
 * copy of this expression would drift from the first.
 */
export const useDesignScope = () => {
    const route = useRoute();

    const inDesignScope = computed(() => {
        const mode = (route.matched.find((m) => m.meta.mode)?.meta.mode ??
            "business") as Mode;
        return groupForMode(mode) === "design";
    });

    return { inDesignScope };
};
